/**
 * Which logo the store should show, scoped to the shop it belongs to.
 *
 * The store's logo URL is not always a copy of `Shop.logoUrl`: for shops whose
 * doc never got the field, `getLogoUrl` derives one from Storage, so a shop
 * snapshot carrying no `logoUrl` must not wipe it. But shops switch without the
 * store being reset (`setCurrentShopId` never calls `unsubscribeShop`), so a
 * carried value is only valid for the shop it was resolved for - otherwise a
 * newly opened shop shows the previous shop's logo and never falls back to the
 * Shop Hero wordmark.
 */
export interface ShopLogoState {
  logoUrl: string | undefined;
  logoShopId: string | undefined;
}

export const resolveShopLogo = (
  shopId: string | undefined,
  docLogoUrl: string | undefined,
  previous: ShopLogoState
): ShopLogoState => {
  if (docLogoUrl) {
    return { logoUrl: docLogoUrl, logoShopId: shopId };
  }

  // An empty string is an explicit clear - `logo.delete()` writes "" rather
  // than removing the field, and `Shop.form.tsx` creates shops with it. Only an
  // *absent* field means "never set", which is the case where a Storage-derived
  // URL may still be valid and worth carrying.
  if (docLogoUrl === "") {
    return { logoUrl: undefined, logoShopId: shopId };
  }

  if (shopId && previous.logoShopId === shopId) {
    return { logoUrl: previous.logoUrl, logoShopId: shopId };
  }

  return { logoUrl: undefined, logoShopId: shopId };
};
